import * as React from "react";
import { Box, Typography, Stack, TextField, Button, IconButton, CircularProgress, Alert } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { fetchMyWorkshopStatus, joinGroup } from "../api/queries";
import { getSession } from "../api/session";
import type { WorkshopStatus } from "../types/workshop";

export default function WorkshopWaiting(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const session = React.useMemo(() => getSession(), []);

  const [status, setStatus] = React.useState<WorkshopStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [groupName, setGroupName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [checking, setChecking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    if (!session) {
      handlePageChange("Entry");
      return;
    }

    fetchMyWorkshopStatus(session.email).then(({ data, error: fetchError }) => {
      if (!mounted) return;
      setLoading(false);

      if (fetchError || !data || !data.chosen_mission_id) {
        handlePageChange("Dashboard");
        return;
      }

      if (data.access_verified) {
        handlePageChange("MissionView");
        return;
      }

      setStatus(data);
      // An organiser may already have planned this person into a group; the
      // access code still has to come from them.
      if (data.group_name) setGroupName(data.group_name);
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCode = async () => {
    if (!session || !groupName.trim() || !code.trim()) return;
    setError(null);
    setChecking(true);

    const { data: ok, error: verifyError } = await joinGroup(groupName.trim(), code.trim());

    setChecking(false);

    if (verifyError) {
      setError(verifyError.message);
      return;
    }

    if (!ok) {
      setError("That group name and access code don't match. Please double-check both and try again.");
      return;
    }

    handlePageChange("MissionView");
  };

  const dt = status?.workshop_starts_at ? new Date(status.workshop_starts_at) : null;
  // "en-US" forces English wording regardless of the system/browser locale;
  // the timezone itself is already the computer's local one by default
  // (no timeZone override), only the language was following system locale.
  const dateStr = dt ? dt.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : null;
  const timeStr = dt ? dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 },
        bgcolor: "#08050b",
        backgroundImage:
          "linear-gradient(90deg, rgba(9, 5, 11, 0.96) 0%, rgba(9, 5, 11, 0.84) 42%, rgba(80, 29, 127, 0.2) 100%), url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: { xs: "62% center", md: "center" },
      }}
    >
      {loading ? (
        <CircularProgress sx={{ color: "#C4B5FD" }} />
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            p: { xs: 2.75, sm: 3.5 },
            borderRadius: 3,
            border: "1px solid rgba(168,85,247,0.28)",
            background: "linear-gradient(160deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)",
            backdropFilter: "blur(14px)",
          }}
        >
          <Stack spacing={2.5}>
            {/* Back to the dashboard: the missions stay viewable (and changeable, until shortly before the workshop). */}
            <IconButton
              onClick={() => handlePageChange("Dashboard")}
              aria-label="Back to dashboard"
              sx={{
                alignSelf: "flex-start",
                color: "#E9D5FF",
                border: "1px solid rgba(168,85,247,0.35)",
                bgcolor: "rgba(255,255,255,0.04)",
                "&:hover": { bgcolor: "rgba(168,85,247,0.16)" },
              }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>

            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  color: "#E9D5FF",
                  background: "linear-gradient(160deg, rgba(124,58,237,0.45), rgba(56,189,248,0.22))",
                  border: "1px solid rgba(196,181,253,0.35)",
                }}
              >
                <EventAvailableRoundedIcon sx={{ fontSize: 28 }} />
              </Box>

              <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 20, sm: 23 } }}>
                Thanks for choosing mission {status?.mission_name}!
              </Typography>
              <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 14, lineHeight: 1.6 }}>
                We can't wait to welcome you at {status?.workshop_name || "the workshop"}
                {dateStr ? (
                  <>
                    {" "}
                    on <strong style={{ color: "#E9D5FF" }}>{dateStr}</strong>
                    {timeStr && (
                      <>
                        {" "}
                        at <strong style={{ color: "#E9D5FF" }}>{timeStr}</strong>
                      </>
                    )}
                  </>
                ) : (
                  ""
                )}
                {status?.workshop_location ? <> in {status.workshop_location}</> : ""}
                . You'll work on the mission sheet together with your group: to open it, enter your group's name and the
                access code you receive from our team during the workshop.
              </Typography>
              {status?.workshop_description && (
                <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 13, lineHeight: 1.6 }}>
                  {status.workshop_description}
                </Typography>
              )}
            </Stack>

            {error && (
              <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              label="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter your group's name"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCode();
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FFFFFF" },
                "& .MuiInputLabel-root": { fontWeight: 800, color: "#374151" },
              }}
            />

            <TextField
              label="Access code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter the code your group received"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCode();
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FFFFFF" },
                "& .MuiInputLabel-root": { fontWeight: 800, color: "#374151" },
              }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={submitCode}
              disabled={checking || !groupName.trim() || !code.trim()}
              sx={{ bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" }, textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {checking ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Unlock the mission sheet"}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
